import { useEffect, useState } from 'react'
import { getRegs } from '../../utils/fc'

function isActive(row, field) {
  return Number(row?.[field]) === 1
}

function normalizeStaticMap(raw) {
  const map = {}
  Object.entries(raw || {}).forEach(([hand, list]) => {
    map[hand] = (list || []).map((c, index) => ({
      id: index,
      nome: c.nome,
      hand: c.hand,
      confianca: c.confianca || 'media',
    }))
  })
  return map
}

async function loadStaticMap() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
  const res = await fetch(`${base}/componenteErp-map.json`, { cache: 'no-cache' })
  if (!res.ok) throw new Error(`componenteErp-map.json: ${res.status}`)
  const raw = await res.json()
  return normalizeStaticMap(raw)
}

/**
 * Carrega mapeamento componenteErp_hand → componentes reais do inventário Oikos.
 * @param {{ source?: 'api' | 'static' }} options
 */
export function useComponenteErpMap({ source = 'api' } = {}) {
  const [byHand, setByHand] = useState({})
  const [status, setStatus] = useState('loading')

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        if (source === 'static') {
          const map = await loadStaticMap()
          if (cancelled) return
          setByHand(map)
          setStatus('ok')
          return
        }

        const [erpRows, links, componentes] = await Promise.all([
          getRegs('componenteErp'),
          getRegs('componenteErp_componente'),
          getRegs('componente'),
        ])

        if (cancelled) return

        const compById = Object.fromEntries(
          (componentes || []).map(c => [Number(c.componente_id), c]),
        )
        const erpHandById = Object.fromEntries(
          (erpRows || []).map(e => [Number(e.componenteErp_id), e.componenteErp_hand]),
        )

        const map = {}
        ;(links || []).forEach((link) => {
          if (!isActive(link, 'componenteErp_componente_ativo')) return
          const hand = erpHandById[Number(link.componenteErp_id)]
          const comp = compById[Number(link.componente_id)]
          if (!hand || !comp || !isActive(comp, 'componente_ativo')) return
          if (!map[hand]) map[hand] = []
          map[hand].push({
            id: Number(comp.componente_id),
            nome: comp.componente_nome,
            hand: comp.componente_hand,
            confianca: link.componenteErp_componente_confianca || 'media',
          })
        })

        Object.values(map).forEach((arr) => {
          arr.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
        })

        setByHand(map)
        setStatus('ok')
      } catch (err) {
        console.warn('componenteErp map:', err)
        if (!cancelled) setStatus('error')
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [source])

  return { byHand, status }
}

export default useComponenteErpMap
